/**
 * Shared attachment UI components used across Chat, Feed, Task Comments, and Task Description.
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FileText, FileSpreadsheet, Archive, File, X, Image as ImageIcon, Music, Video, Download, Play } from 'lucide-react';
import { validateFiles, ACCEPT_STRING } from '../../lib/useSpacesUpload';

// ─── helpers ────────────────────────────────────────────────────────────────

export function isImage(ct) { return ct && ct.startsWith('image/'); }
export function isVideo(ct) { return ct && ct.startsWith('video/'); }
export function isAudio(ct) { return ct && ct.startsWith('audio/'); }

// Shortens a long file name to fit a chip while always keeping the
// extension visible (e.g. "GLET_AttendanceRegi…xlsx" not "GLET_AttendanceRegist…"),
// since the extension is the one part of the name a user actually needs
// to recognise the file type at a glance.
export function truncateFileName(name, maxLen = 22) {
  if (!name) return '';
  if (name.length <= maxLen) return name;
  const dot = name.lastIndexOf('.');
  const extLen = name.length - dot;
  if (dot <= 0 || extLen > 8) return `${name.slice(0, maxLen - 1)}…`;
  const ext = name.slice(dot);
  const keep = Math.max(4, maxLen - ext.length - 1);
  return `${name.slice(0, keep)}…${ext}`;
}

function fileIcon(ct, size = 14) {
  if (!ct) return <File size={size} />;
  if (ct.startsWith('image/')) return <ImageIcon size={size} />;
  if (ct.startsWith('video/')) return <Video size={size} />;
  if (ct.startsWith('audio/')) return <Music size={size} />;
  if (ct.includes('spreadsheet') || ct.includes('excel') || ct === 'text/csv') return <FileSpreadsheet size={size} />;
  if (ct === 'application/zip') return <Archive size={size} />;
  return <FileText size={size} />;
}

function ExcelViewer({ url }) {
  const [sheets, setSheets] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setSheets(null); setError(null);
    (async () => {
      try {
        const XLSX = await import('xlsx');
        const res = await fetch(url);
        const ab = await res.arrayBuffer();
        const wb = XLSX.read(ab, { type: 'array' });
        const parsed = wb.SheetNames.map(name => ({
          name,
          html: XLSX.utils.sheet_to_html(wb.Sheets[name]),
        }));
        if (!cancelled) { setSheets(parsed); setActiveSheet(0); }
      } catch (e) {
        if (!cancelled) setError('Could not load spreadsheet.');
      }
    })();
    return () => { cancelled = true; };
  }, [url]);

  if (error) return <div style={{ color: '#f87171', padding: 32 }}>{error}</div>;
  if (!sheets) return <div style={{ color: '#aaa', padding: 32 }}>Loading…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '88vw', height: 'calc(90vh - 56px)', background: '#fff' }}>
      {sheets.length > 1 && (
        <div style={{ display: 'flex', gap: 2, padding: '4px 8px', background: '#f3f4f6', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          {sheets.map((s, i) => (
            <button key={i} onClick={() => setActiveSheet(i)} style={{
              padding: '3px 12px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 12,
              background: i === activeSheet ? '#1a5276' : 'transparent',
              color: i === activeSheet ? '#fff' : '#374151',
              fontWeight: i === activeSheet ? 600 : 400,
            }}>{s.name}</button>
          ))}
        </div>
      )}
      <style>{`
        .att-excel-wrap table { border-collapse: collapse; font-size: 12px; color: #111; }
        .att-excel-wrap td, .att-excel-wrap th { border: 1px solid #d1d5db; padding: 4px 8px; white-space: nowrap; }
        .att-excel-wrap th { background: #f9fafb; font-weight: 600; }
      `}</style>
      <div className="att-excel-wrap" style={{ overflow: 'auto', flex: 1, padding: 8 }}
        dangerouslySetInnerHTML={{ __html: sheets[activeSheet].html }}
      />
    </div>
  );
}

async function triggerDownload(url, filename) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
  } catch {
    window.open(url, '_blank');
  }
}

// ─── Text file viewer (CSV / TXT) ────────────────────────────────────────────

function TextFileViewer({ url, name, onDownload }) {
  const [text, setText] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    fetch(url)
      .then(r => r.text())
      .then(setText)
      .catch(() => setErr(true));
  }, [url]);
  if (err) return (
    <div style={{ padding: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <span style={{ color: '#ccc', fontSize: 14 }}>Could not load file</span>
      <button onClick={onDownload} style={{ background: 'none', border: 'none', color: '#4f8ef7', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: 0 }}>
        <Download size={14} /> Download
      </button>
    </div>
  );
  if (text === null) return <div style={{ color: '#888', padding: 32 }}>Loading…</div>;
  return (
    <pre style={{
      width: '88vw', height: 'calc(90vh - 56px)', overflow: 'auto',
      background: '#1a1a1a', color: '#e0e0e0', fontSize: 13, lineHeight: 1.6,
      padding: '16px 20px', margin: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
    }}>
      {text}
    </pre>
  );
}

// ─── Attachment viewer modal ─────────────────────────────────────────────────

export function AttachmentModal({ attachment, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const a = attachment;
  // Portal to <body>: rendered in place, the overlay is trapped in the page
  // content's stacking context and the sidebar paints over it.
  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.82)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', background: 'rgba(0,0,0,0.5)', borderRadius: '8px 8px 0 0',
          gap: 12, minWidth: 0,
        }}>
          <span title={a.file_name} style={{ fontSize: 13, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {a.file_name}
          </span>
          <button
            onClick={() => triggerDownload(a.file_url, a.file_name)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', padding: 4, flexShrink: 0 }}
            title="Download"
          >
            <Download size={15} />
          </button>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', padding: 4, flexShrink: 0 }}
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ background: '#111', borderRadius: '0 0 8px 8px', overflow: 'hidden', maxHeight: 'calc(90vh - 48px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isImage(a.content_type) && (
            <img src={a.file_url} alt={a.file_name} style={{ maxWidth: '88vw', maxHeight: 'calc(90vh - 56px)', objectFit: 'contain', display: 'block' }} />
          )}
          {isVideo(a.content_type) && (
            <video src={a.file_url} controls autoPlay style={{ maxWidth: '88vw', maxHeight: 'calc(90vh - 56px)' }} />
          )}
          {isAudio(a.content_type) && (
            <div style={{ padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              <Music size={48} color="#888" />
              <span style={{ color: '#ccc', fontSize: 14 }}>{a.file_name}</span>
              <audio src={a.file_url} controls autoPlay />
            </div>
          )}
          {!isImage(a.content_type) && !isVideo(a.content_type) && !isAudio(a.content_type) && (() => {
            const ct = a.content_type || '';
            const name = (a.file_name || '').toLowerCase();
            const isPdf = ct === 'application/pdf' || name.endsWith('.pdf');
            const isExcel = ct.includes('spreadsheet') || ct.includes('excel') || name.endsWith('.xlsx') || name.endsWith('.xls');
            const isCsv = ct === 'text/csv' || name.endsWith('.csv');
            const isTxt = ct === 'text/plain' || name.endsWith('.txt');
            if (isPdf) {
              return (
                <iframe
                  src={a.file_url}
                  title={a.file_name}
                  style={{ width: '88vw', height: 'calc(90vh - 56px)', border: 'none', background: '#fff', display: 'block' }}
                />
              );
            }
            if (isExcel) {
              return <ExcelViewer url={a.file_url} />;
            }
            if (isCsv || isTxt) {
              return <TextFileViewer url={a.file_url} name={a.file_name} onDownload={() => triggerDownload(a.file_url, a.file_name)} />;
            }
            return (
              <div style={{ padding: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                {fileIcon(a.content_type, 48)}
                <span style={{ color: '#ccc', fontSize: 14 }}>{a.file_name}</span>
                <button onClick={() => triggerDownload(a.file_url, a.file_name)} style={{ background: 'none', border: 'none', color: '#4f8ef7', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: 0 }}>
                  <Download size={14} /> Download
                </button>
              </div>
            );
          })()}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Sent attachment display ─────────────────────────────────────────────────

/**
 * Renders sent attachments: images inline, video/audio as player, others as styled chip.
 * Pass `onDelete` to show a delete button (for owners/admins).
 */
export function AttachmentDisplay({ attachments, onDelete }) {
  const [modalAtt, setModalAtt] = useState(null);
  if (!attachments?.length) return null;

  return (
    <>
      {modalAtt && <AttachmentModal attachment={modalAtt} onClose={() => setModalAtt(null)} />}
      <div className="att-display-list">
        {attachments.map((a, i) => {
          if (isImage(a.content_type)) {
            return (
              <div key={i} className="att-display-img-wrap">
                <img
                  src={a.file_url}
                  alt={a.file_name}
                  className="att-display-img"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setModalAtt(a)}
                />
                {onDelete && (
                  <button type="button" className="att-display-del" onClick={() => onDelete(a)} title="Remove">
                    <X size={10} />
                  </button>
                )}
              </div>
            );
          }

          if (isVideo(a.content_type)) {
            return (
              <div key={i} className="att-display-img-wrap att-display-vid-wrap" onClick={() => setModalAtt(a)}>
                <video src={`${a.file_url}#t=0.1`} className="att-display-img att-display-video-thumb" preload="metadata" muted playsInline />
                <div className="att-display-vid-play"><Play size={18} fill="#fff" /></div>
                {onDelete && (
                  <button type="button" className="att-display-del" onClick={(e) => { e.stopPropagation(); onDelete(a); }} title="Remove">
                    <X size={10} />
                  </button>
                )}
              </div>
            );
          }

          if (isAudio(a.content_type)) {
            return (
              <div key={i} className="att-display-media-wrap">
                <div className="att-display-audio-label">
                  <Music size={13} />
                  <span title={a.file_name}>{a.file_name}</span>
                </div>
                <audio src={a.file_url} controls className="att-display-audio" />
                {onDelete && (
                  <button type="button" className="att-display-del" onClick={() => onDelete(a)} title="Remove">
                    <X size={10} />
                  </button>
                )}
              </div>
            );
          }

          // File chip — opens modal
          return (
            <div
              key={i}
              className="att-display-file"
              style={{ cursor: 'pointer' }}
              onClick={() => setModalAtt(a)}
            >
              {fileIcon(a.content_type)}
              <span className="att-display-file-name" title={a.file_name}>
                {truncateFileName(a.file_name, 26)}
              </span>
              {onDelete && (
                <button type="button" className="att-display-del-inline" onClick={(e) => { e.stopPropagation(); onDelete(a); }} title="Remove">
                  <X size={10} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ─── Pending file preview ─────────────────────────────────────────────────────

/**
 * Shows preview thumbnails for selected files before upload.
 * Images show as thumbnail, others show as icon + name chip.
 */
export function PendingFilePreview({ files, onRemove, errors }) {
  const [modalAtt, setModalAtt] = useState(null);

  const previews = useMemo(() => {
    return files.map(f => ({
      file: f,
      previewUrl: isImage(f.type) ? URL.createObjectURL(f) : null,
    }));
  }, [files]);

  const openPreview = (file) => {
    const url = URL.createObjectURL(file);
    setModalAtt({ file_url: url, file_name: file.name, content_type: file.type, _blobUrl: url });
  };

  const closeModal = () => {
    if (modalAtt?._blobUrl) URL.revokeObjectURL(modalAtt._blobUrl);
    setModalAtt(null);
  };

  if (!files?.length && !errors?.length) return null;

  // Keep original index alongside each preview so removal is always correct
  const indexed = previews.map((p, i) => ({ ...p, globalIdx: i }));
  const images = indexed.filter(p => p.previewUrl);
  const nonImages = indexed.filter(p => !p.previewUrl);

  return (
    <div className="att-pending-wrap">
      {modalAtt && <AttachmentModal attachment={modalAtt} onClose={closeModal} />}
      {errors?.length > 0 && (
        <div className="att-pending-errors">
          {errors.map((e, i) => <div key={i} className="att-pending-error">{e}</div>)}
        </div>
      )}
      {/* Large image previews */}
      {images.length > 0 && (
        <div className="att-pending-images">
          {images.map(({ file, previewUrl, globalIdx }) => (
            <div key={globalIdx} className="att-pending-img-card" onClick={() => openPreview(file)}>
              <img
                src={previewUrl}
                alt={file.name}
                className="att-pending-img-large"
                onLoad={() => URL.revokeObjectURL(previewUrl)}
              />
              <div className="att-pending-img-name" title={file.name}>{file.name}</div>
              <button className="att-pending-img-remove" onClick={(e) => { e.stopPropagation(); onRemove(globalIdx); }} title="Remove">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      {/* File chips for non-images */}
      {nonImages.length > 0 && (
        <div className="att-pending-list">
          {nonImages.map(({ file, globalIdx }) => (
            <div key={globalIdx} className="att-pending-item" onClick={() => openPreview(file)} style={{ cursor: 'pointer' }}>
              <div className="att-pending-icon">{fileIcon(file.type, 16)}</div>
              <span className="att-pending-name" title={file.name}>{truncateFileName(file.name, 18)}</span>
              <button className="att-pending-remove" onClick={(e) => { e.stopPropagation(); onRemove(globalIdx); }} title="Remove">
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { ACCEPT_STRING };

// ─── Mixed content renderer ───────────────────────────────────────────────────

/**
 * Renders body text interleaved with attachments using [[att:N]] markers.
 *
 * If the body contains [[att:0]], [[att:1]] etc., each marker is replaced with
 * the corresponding attachment rendered inline. Any attachments not referenced
 * by a marker (or if there are no markers at all) are rendered after the text,
 * preserving backward compatibility with existing messages.
 *
 * renderText(segment) — function to render a plain-text segment (e.g. renderMentionText)
 */
export function renderMixedContent(body, attachments, renderText, onDelete) {
  if (!attachments?.length) {
    return body ? renderText(body) : null;
  }

  const MARKER_RE = /\[\[(?:id|att):(\d+)\]\]/g;
  const hasMarkers = MARKER_RE.test(body || '');
  MARKER_RE.lastIndex = 0;

  if (!hasMarkers) {
    // No markers — text then all attachments below
    return (
      <>
        {body ? <span style={{ whiteSpace: 'pre-wrap' }}>{renderText(body)}</span> : null}
        <AttachmentDisplay attachments={attachments} onDelete={onDelete} />
      </>
    );
  }

  // Build lookup maps
  const attById = {};
  attachments.forEach(a => { if (a.id != null) attById[String(a.id)] = a; });

  // Split body on [[id:X]] (new) and [[att:N]] (legacy index) markers
  const parts = [];
  let lastIndex = 0;
  let match;
  const usedAttIds = new Set();

  MARKER_RE.lastIndex = 0;
  while ((match = MARKER_RE.exec(body)) !== null) {
    const isIdBased = match[0].startsWith('[[id:');
    const n = parseInt(match[1], 10);
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: body.slice(lastIndex, match.index) });
    }
    // Resolve attachment: id-based uses attById; legacy uses positional index
    const att = isIdBased ? attById[String(n)] : attachments[n];
    parts.push({ type: 'att', att });
    if (att?.id != null) usedAttIds.add(att.id);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < (body || '').length) {
    parts.push({ type: 'text', value: body.slice(lastIndex) });
  }

  // Any attachments not referenced by any marker
  const trailing = attachments.filter(a => a.id != null && !usedAttIds.has(a.id));

  return (
    <>
      {parts.map((part, i) => {
        if (part.type === 'text') {
          return part.value
            ? <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{renderText(part.value)}</span>
            : null;
        }
        if (!part.att) return null;
        return (
          <div key={i} style={{ margin: '4px 0' }}>
            <AttachmentDisplay attachments={[part.att]} onDelete={onDelete ? () => onDelete(part.att) : undefined} />
          </div>
        );
      })}
      {trailing.length > 0 && <AttachmentDisplay attachments={trailing} onDelete={onDelete} />}
    </>
  );
}
